import React from 'react';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import type { KpiViewMode } from './line-item-daily-kpis';
import { Last7DaysDailyKpisLayout } from './last-7-days-daily-kpis';

type Props = {
  setViewMode: (mode: KpiViewMode) => void;
};

export function Last7DaysSpendKpis({ setViewMode }: Props) {
  const { lineItem } = useLineItemContext();

  const result = useCubeQuery({
    dimensions: ['line_items.unit_price_type_name', 'line_item_metrics_daily.line_item_id'],
    measures: [
      'line_item_metrics_daily.actual_spend_yesterday',
      'line_item_metrics_daily.planned_spend_yesterday',
      'line_item_metrics_daily.avg_actual_spend_last_3_days',
      'line_item_metrics_daily.avg_actual_spend_last_7_days',
      'line_item_metrics_daily.actual_spend_last_7_days',
      'line_item_metrics_daily.planned_spend_last_7_days'
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  if (result.status === 'loading') {
    return (
      <Last7DaysDailyKpisLayout
        viewMode="spend"
        setViewMode={setViewMode}
        loading
        unitAbbr=""
        yesterday={{ formatted: DASH, actual: null, planned: null }}
        avg3Day={DASH}
        avg7Day={DASH}
        variance={{ actual: 0, planned: 0, formattedDifference: '0' }}
      />
    );
  }

  if (result.status === 'error' || !result.rows[0] || !result.formattedRows[0]) {
    return (
      <Last7DaysDailyKpisLayout
        viewMode="spend"
        setViewMode={setViewMode}
        unitAbbr=""
        yesterday={{ formatted: DASH, actual: null, planned: null }}
        avg3Day={DASH}
        avg7Day={DASH}
        variance={{ actual: 0, planned: 0, formattedDifference: '0' }}
      />
    );
  }

  const row = result.rows[0];
  const formatted = result.formattedRows[0];

  const actual7d = Number(row['line_item_metrics_daily.actual_spend_last_7_days']);
  const planned7d = Number(row['line_item_metrics_daily.planned_spend_last_7_days']);

  return (
    <Last7DaysDailyKpisLayout
      viewMode="spend"
      setViewMode={setViewMode}
      unitAbbr=""
      yesterday={{
        formatted: formatted['line_item_metrics_daily.actual_spend_yesterday'],
        actual: Number(row['line_item_metrics_daily.actual_spend_yesterday']),
        planned: Number(row['line_item_metrics_daily.planned_spend_yesterday'])
      }}
      avg3Day={formatted['line_item_metrics_daily.avg_actual_spend_last_3_days']}
      avg7Day={formatted['line_item_metrics_daily.avg_actual_spend_last_7_days']}
      variance={{
        actual: actual7d,
        planned: planned7d,
        formattedDifference: formatMoney(Math.abs(actual7d - planned7d))
      }}
    />
  );
}

const DASH = '—';
